import type { CustomerUsage } from "@/features/customers/api";
import { cn } from "@/lib/utils";

/** How close to the edge the allowance is — one definition, used for every
 *  colour and every warning line, so the tenant's page and the customer's page
 *  never disagree about whether things are fine.
 *
 *  Matches the server's alert thresholds (apps/customers/usage.py): warn at 80,
 *  press at 90, stop at 100. */
export type MinuteLevel = "ok" | "warn" | "critical" | "empty";

export function minuteLevel(u: CustomerUsage): MinuteLevel {
  if (!u.metered) return "ok";
  if (u.blocked || u.minutes_left <= 0) return "empty";
  if (u.percent_used >= 90) return "critical";
  if (u.percent_used >= 80) return "warn";
  return "ok";
}

const BAR: Record<MinuteLevel, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  critical: "bg-orange-500",
  empty: "bg-destructive",
};

const TEXT: Record<MinuteLevel, string> = {
  ok: "text-muted-foreground",
  warn: "text-amber-600 dark:text-amber-400",
  critical: "text-orange-600 dark:text-orange-400",
  empty: "text-destructive",
};

export const minuteTextClass = (level: MinuteLevel) => TEXT[level];

export const fmtMinutes = (n: number) => n.toLocaleString();

/** The bar itself. `used` can exceed the allowance — the last call of a period
 *  always finishes, so a customer can end a month slightly over. */
export function MinutesBar({
  usage,
  className,
}: {
  usage: CustomerUsage;
  className?: string;
}) {
  const level = minuteLevel(usage);
  const pct = Math.min(usage.percent_used, 100);
  return (
    <div
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
      aria-valuenow={usage.minutes_used}
      aria-valuemin={0}
      aria-valuemax={usage.minutes_allowed}
      aria-label="Call minutes used"
    >
      <div
        className={cn("h-full rounded-full transition-[width]", BAR[level])}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Compact "13 / 200 min" + bar, for a table row. */
export function MinutesCell({ usage }: { usage: CustomerUsage }) {
  if (!usage.metered) {
    return (
      <div className="text-xs text-muted-foreground">
        {fmtMinutes(usage.minutes_used)} min
        <div className="text-[11px]">no plan · not limited</div>
      </div>
    );
  }
  const level = minuteLevel(usage);
  return (
    <div className="min-w-[7.5rem] space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium tabular-nums">
          {fmtMinutes(usage.minutes_used)}
          <span className="text-muted-foreground">
            {" / "}
            {fmtMinutes(usage.minutes_allowed)}
          </span>
        </span>
        <span className={cn("tabular-nums", minuteTextClass(level))}>
          {level === "empty" ? "none left" : `${fmtMinutes(usage.minutes_left)} left`}
        </span>
      </div>
      <MinutesBar usage={usage} />
    </div>
  );
}
